"use client";

import Link from "next/link";
import { Globe, Network, Layers, Users, RefreshCw, Cpu, Server } from "@/components/icons";
import type { LucideIcon } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";

type Category = {
  icon: LucideIcon;
  title: string;
  description: string;
  types: string[];
};

const CATEGORIES: Category[] = [
  {
    icon: Globe,
    title: "By Network Source",
    description: "Where the IP address actually originates from on the internet.",
    types: ["Residential", "Mobile", "Datacenter", "ISP"],
  },
  {
    icon: Layers,
    title: "By Role",
    description: "Where the proxy sits relative to the client and target server.",
    types: ["Forward proxy", "Reverse proxy"],
  },
  {
    icon: Network,
    title: "By Protocol",
    description: "The networking protocol the proxy speaks with your client.",
    types: ["HTTP", "HTTP(S) tunnelling", "SOCKS5"],
  },
  {
    icon: Users,
    title: "By Access Model",
    description: "How many users share a single IP at the same time.",
    types: ["Shared", "Semi-dedicated", "Dedicated"],
  },
  {
    icon: RefreshCw,
    title: "By Session Behavior",
    description: "How and when the proxy's exit IP changes per request.",
    types: ["Rotating", "Sticky", "Static"],
  },
  {
    icon: Cpu,
    title: "By Address Family",
    description: "The underlying IP protocol version the proxy uses.",
    types: ["IPv4", "IPv6"],
  },
  {
    icon: Server,
    title: "By Deployment",
    description: "Whether the proxy runs on your own hardware or a managed cloud network.",
    types: ["Self-hosted", "Managed cloud"],
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

export default function CategoryArchive() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="proxy-categories-heading"
      className="border-border py-section-sm relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Proxy categories</p>
          <h2
            id="proxy-categories-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Proxy types, organized by dimension
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Proxy types are explained across separate dimensions, which prevents the common problem
            of comparing categories that answer different questions. For example,{" "}
            <Link
              href="/proxy-server/socks-proxy"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              SOCKS5
            </Link>{" "}
            is a protocol, residential is a network source, and rotating is session behavior — a
            proxy can be all three at once.
          </p>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
            Deep dives:{" "}
            <Link
              href="/proxy-server/http-proxies"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              HTTP proxies
            </Link>
            ,{" "}
            <Link
              href="/proxy-server/isp-proxies"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              ISP proxies
            </Link>
            , and{" "}
            <Link
              href="/proxy-server/backconnect-proxy"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              backconnect proxies
            </Link>
            .
          </p>
        </div>

        <motion.ul
          variants={containerVariants}
          initial={reduced ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
        >
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <motion.li key={cat.title} variants={cardVariants}>
                <div className="border-border bg-card relative flex h-full flex-col overflow-hidden rounded-2xl border p-5">
                  <div className="relative mb-4">
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-lg border"
                      style={{
                        background: "color-mix(in srgb, var(--brand-green) 6%, transparent)",
                        borderColor: "color-mix(in srgb, var(--brand-green) 20%, transparent)",
                      }}
                    >
                      <Icon size={18} aria-hidden="true" style={{ color: "var(--brand-green)" }} />
                    </span>
                  </div>

                  <h3 className="text-foreground relative mb-1.5 text-lg font-semibold">
                    {cat.title}
                  </h3>
                  <p className="text-muted-foreground relative mb-4 text-sm leading-relaxed">
                    {cat.description}
                  </p>

                  <div className="relative mt-auto flex flex-wrap gap-1.5">
                    {cat.types.map((t) => (
                      <span
                        key={t}
                        className="border-border text-muted-foreground bg-background/40 rounded-md border px-2 py-1 text-[11px] font-medium"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}
